import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './api';
import { StatCard, MiniBar, Spinner, ErrorBox, fmtUptime } from './components';

/* ── Add Host Dialog ────────────────────────────────────────────────────────── */
function AddHostDialog({ onClose, onAdded }) {
  const [label, setLabel] = useState('');
  const [ip, setIp]       = useState('');
  const [port, setPort]   = useState('9101');
  const [err, setErr]     = useState('');

  const submit = async () => {
    if (!ip) { setErr('IP address required'); return; }
    try {
      await api.post('/hosts', { label, ip, port: parseInt(port) || 9101 });
      onAdded();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to add host');
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-box" onClick={e => e.stopPropagation()}>
        <div className="dialog-title">ADD HOST</div>
        {err && <div style={{ color: 'var(--danger)', fontFamily: 'var(--mono)',
                              fontSize: 11, marginBottom: 14 }}>[ {err} ]</div>}
        <div className="dialog-field">
          <label className="dialog-label">LABEL (optional)</label>
          <input className="inp" value={label} onChange={e => setLabel(e.target.value)}
                 placeholder="My Server" />
        </div>
        <div className="dialog-field">
          <label className="dialog-label">IP ADDRESS *</label>
          <input className="inp" value={ip} onChange={e => setIp(e.target.value)}
                 placeholder="192.168.1.100" autoFocus />
        </div>
        <div className="dialog-field">
          <label className="dialog-label">AGENT PORT</label>
          <input className="inp" value={port} onChange={e => setPort(e.target.value)}
                 placeholder="9101" />
        </div>
        <div className="dialog-actions">
          <button className="btn primary" onClick={submit}>ADD HOST</button>
          <button className="btn" onClick={onClose}>CANCEL</button>
        </div>
      </div>
    </div>
  );
}

/* ── Host Card ─────────────────────────────────────────────────────────────── */
function HostCard({ host, onDelete, isAdmin }) {
  const navigate = useNavigate();
  const online   = host.online;
  const sys      = host.data || {};
  const ram      = sys.ram || {};
  const cpu_pct  = sys.load_avg ? Math.min(sys.load_avg[0] / (sys.cpu?.cores_logical || 1) * 100, 100) : null;

  const handleDelete = async e => {
    e.stopPropagation();
    if (!window.confirm(`Remove ${host.label}?`)) return;
    await api.delete(`/hosts/${host.id}`);
    onDelete(host.id);
  };

  return (
    <div className={`host-card${online ? '' : ' offline'}`}
         onClick={() => online && navigate(`/host/${host.id}`)}>
      <div className={`host-status ${online ? 'online' : 'offline'}`} />
      <div className="host-card-name">{host.label}</div>
      <div className="host-card-ip">{host.ip}:{host.port}</div>

      {online ? (
        <>
          <div className="host-card-stats">
            <div>
              <div className="host-card-stat-label">CPU LOAD</div>
              <div className="host-card-stat-value">
                {cpu_pct !== null ? `${cpu_pct.toFixed(1)}%` : '—'}
              </div>
              {cpu_pct !== null && <MiniBar pct={cpu_pct} />}
            </div>
            <div>
              <div className="host-card-stat-label">RAM</div>
              <div className="host-card-stat-value">
                {ram.percent !== undefined ? `${ram.percent}%` : '—'}
              </div>
              {ram.percent !== undefined && <MiniBar pct={ram.percent} />}
            </div>
          </div>
          <div style={{ marginTop: 12, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
            {sys.hostname && <span>{sys.hostname} &nbsp;·&nbsp; </span>}
            {sys.uptime_secs && fmtUptime(sys.uptime_secs)}
          </div>
        </>
      ) : (
        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--danger)', marginTop: 8 }}>
          UNREACHABLE
        </div>
      )}

      {isAdmin && (
        <div className="host-card-actions">
          <button className="btn danger" style={{ fontSize: 11, padding: '4px 10px' }}
                  onClick={handleDelete}>REMOVE</button>
        </div>
      )}
    </div>
  );
}

const REFRESH_OPTIONS = [
  { label: '5s',  value: 5000 },
  { label: '10s', value: 10000 },
  { label: '15s', value: 15000 },
  { label: '30s', value: 30000 },
  { label: '60s', value: 60000 },
  { label: 'Off', value: 0 },
];

/* ── Overview Page ─────────────────────────────────────────────────────────── */
export default function OverviewPage({ user }) {
  const [hosts, setHosts]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [showAdd, setShowAdd]   = useState(false);
  const [refresh, setRefresh]   = useState(15000);
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    try {
      const r = await api.get('/status');
      setHosts(r.data);
    } catch (e) {
      setError('Failed to load hosts');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    if (!refresh) return;
    const t = setInterval(load, refresh);
    return () => clearInterval(t);
  }, [load, refresh]);

  const onDelete = id => setHosts(h => h.filter(x => x.id !== id));

  return (
    <div className="overview-wrap">
      <div className="overview-toolbar">
        <h1>HOSTS</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>REFRESH</span>
          <div style={{ display: 'flex', gap: 0 }}>
            {REFRESH_OPTIONS.map(o => (
              <button key={o.value}
                style={{
                  background: refresh === o.value ? 'var(--border)' : 'none',
                  border: '1px solid var(--border)',
                  borderLeft: o.value === REFRESH_OPTIONS[0].value ? '1px solid var(--border)' : 'none',
                  color: refresh === o.value ? 'var(--accent)' : 'var(--muted)',
                  padding: '5px 10px',
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  cursor: 'pointer',
                  transition: 'color 0.15s, background 0.15s',
                }}
                onClick={() => setRefresh(o.value)}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
        {isAdmin && (
          <button className="btn primary" onClick={() => setShowAdd(true)}>+ ADD HOST</button>
        )}
        <button className="btn" onClick={load}>↻ REFRESH</button>
      </div>

      {loading && <Spinner />}
      {error   && <ErrorBox msg={error} />}

      <div className="host-grid">
        {hosts.map(h => (
          <HostCard key={h.id} host={h} onDelete={onDelete} isAdmin={isAdmin} />
        ))}
        {!loading && hosts.length === 0 && (
          <div style={{ color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 12,
                        gridColumn: '1 / -1', padding: 32, textAlign: 'center' }}>
            No hosts configured.{isAdmin ? ' Click + ADD HOST to begin.' : ''}
          </div>
        )}
      </div>

      {showAdd && (
        <AddHostDialog onClose={() => setShowAdd(false)} onAdded={load} />
      )}
    </div>
  );
}
